import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Truck, ShieldCheck, RefreshCw, Heart, Minus, Plus, X, Lock, Gem, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatINR, useSEO, pushRecentlyViewed, getRecentlyViewed } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { Reveal } from "@/components/Reveal";
import { ProductCard } from "@/components/ProductCard";
import { LaunchCountdown } from "@/components/LaunchCountdown";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const EDITORIAL = [
  { t: "Premium Stainless Steel", b: "Each frame is precision-machined and mirror-polished — flawless edges, signature engraving, and a jewellery-grade finish.", idx: 0 },
  { t: "Precision Gold Hinges", b: "Machined hardware and a VYRON-engraved bridge give the Launch Edition its unmistakable, tactile luxury feel.", idx: 2 },
  { t: "UV400 Polarized Lenses", b: "True UV400 polarization delivers deep clarity, richer colour, and full UVA/UVB protection — everyday, everywhere.", idx: 1 },
  { t: "Made for Everyday Luxury", b: "Balanced weight distribution and a refined silhouette make it effortless from sunrise to the last light of day.", idx: 3 },
];

export default function Product() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { has, toggle } = useWishlist();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [active, setActive] = useState(0);
  const [color, setColor] = useState("");
  const [size, setSize] = useState("");
  const [qty, setQty] = useState(1);
  const [zoom, setZoom] = useState({ on: false, x: 50, y: 50 });
  const [full, setFull] = useState(false);
  const [form, setForm] = useState({ name: "", rating: 5, title: "", body: "" });
  const imgRef = useRef(null);

  useSEO(
    product?.name,
    product ? `${product.name} — ${product.tagline}. ${product.description?.slice(0, 120)}` : "",
    product
      ? {
          type: "product",
          image: product.images?.[0],
          jsonLd: {
            "@context": "https://schema.org",
            "@type": "Product",
            name: product.name,
            description: product.description,
            image: product.images,
            brand: { "@type": "Brand", name: "VYRON" },
            aggregateRating: { "@type": "AggregateRating", ratingValue: product.rating, reviewCount: product.review_count },
            offers: { "@type": "Offer", priceCurrency: "INR", price: product.price, availability: "https://schema.org/InStock" },
          },
        }
      : {}
  );

  const load = () => {
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      setColor(r.data.colors?.[0] || "");
      setSize(r.data.sizes?.[0] || "");
      setActive(0);
      pushRecentlyViewed(r.data);
    }).catch(() => navigate("/collections"));
    api.get(`/reviews?product_slug=${slug}`).then((r) => setReviews(r.data)).catch(() => {});
    api.get(`/products/${slug}/related`).then((r) => setRelated(r.data)).catch(() => {});
  };
  useEffect(() => { window.scrollTo(0, 0); load(); }, [slug]); // eslint-disable-line

  if (!product) return <div className="pt-40 text-center text-muted-foreground min-h-[60vh]">Loading…</div>;

  const wished = has(product.slug);
  const recent = getRecentlyViewed().filter((p) => p.slug !== slug).slice(0, 4);
  const buyNow = () => { addItem(product, qty); navigate("/checkout"); };
  const onMove = (e) => {
    const r = imgRef.current.getBoundingClientRect();
    setZoom({ on: true, x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!form.name || !form.body) return toast.error("Please add your name and review.");
    try {
      await api.post("/reviews", { ...form, product_slug: slug, rating: Number(form.rating) });
      toast.success("Thank you — your review is live.");
      setForm({ name: "", rating: 5, title: "", body: "" });
      load();
    } catch { toast.error("Could not submit review."); }
  };

  return (
    <div data-testid="product-page" className="pt-24">
      {/* Main */}
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 pt-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Gallery */}
          <div className="flex flex-col-reverse md:flex-row gap-4 lg:sticky lg:top-24 self-start">
            <div className="flex md:flex-col gap-3">
              {product.images.map((img, i) => (
                <button key={i} onClick={() => setActive(i)} data-testid={`thumb-${i}`}
                  className={`w-16 h-20 md:w-20 md:h-24 rounded-2xl overflow-hidden bg-[#F5F5F5] border-2 transition-colors ${active === i ? "border-gold" : "border-transparent"}`}>
                  <img src={`${img}?w=200&q=70`} alt="" className="w-full h-full object-contain" />
                </button>
              ))}
            </div>
            <div
              ref={imgRef}
              onMouseMove={onMove}
              onMouseLeave={() => setZoom((z) => ({ ...z, on: false }))}
              onClick={() => setFull(true)}
              className="flex-1 aspect-[4/5] rounded-3xl overflow-hidden bg-[#F5F5F5] cursor-zoom-in"
              data-testid="product-gallery"
            >
              <motion.img
                key={active}
                initial={{ opacity: 0.5 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}
                src={`${product.images[active]}?w=1400&q=90`}
                alt={product.name}
                className="w-full h-full object-contain transition-transform duration-200"
                style={zoom.on ? { transform: "scale(2)", transformOrigin: `${zoom.x}% ${zoom.y}%` } : { transform: "scale(1)" }}
                data-testid="product-main-image"
              />
            </div>
          </div>

          {/* Info */}
          <div className="lg:py-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={14} className={i < Math.round(product.rating) ? "fill-black text-black" : "text-black/20"} />
                ))}
              </div>
              <span className="text-xs text-muted-foreground">{product.rating} · {product.review_count} reviews</span>
            </div>
            {product.badge && (
              <span className="inline-block text-[11px] uppercase tracking-[0.2em] border border-gold text-gold rounded-full px-4 py-1.5 mb-4" data-testid="product-badge">{product.badge}</span>
            )}
            <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">{product.tagline}</p>
            <h1 className="font-display text-4xl md:text-6xl tracking-[-0.03em] leading-none mb-5">{product.name}</h1>
            <div className="flex flex-wrap items-baseline gap-3 mb-3">
              <span className="text-3xl font-medium" data-testid="product-price">{formatINR(product.price)}</span>
              {product.compare_at_price && (
                <>
                  <span className="text-lg text-muted-foreground line-through">{formatINR(product.compare_at_price)}</span>
                  <span className="text-[11px] uppercase tracking-[0.15em] bg-black text-white rounded-full px-3 py-1" data-testid="product-discount">
                    {Math.round((1 - product.price / product.compare_at_price) * 100)}% OFF
                  </span>
                </>
              )}
            </div>
            {product.compare_at_price && (
              <p className="text-sm text-gold mb-6" data-testid="product-savings">
                You save {formatINR(product.compare_at_price - product.price)}
              </p>
            )}

            {/* Launch countdown */}
            <div className="mb-7">
              <LaunchCountdown testid="product-countdown" />
            </div>

            <p className="text-muted-foreground leading-relaxed max-w-lg mb-8">{product.description}</p>

            {/* Colors */}
            {product.colors?.length > 0 && (
              <div className="mb-6">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Colour — <span className="text-black">{color}</span></p>
                <div className="flex flex-wrap gap-2">
                  {product.colors.map((c) => (
                    <button key={c} onClick={() => setColor(c)} data-testid={`color-${c}`}
                      className={`text-xs uppercase tracking-widest px-4 py-2.5 rounded-full border transition-colors ${color === c ? "bg-black text-white border-black" : "border-black/20 hover:border-black"}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {/* Sizes */}
            {product.sizes?.length > 0 && (
              <div className="mb-6">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Size</p>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((s) => (
                    <button key={s} onClick={() => setSize(s)} data-testid={`size-${s.replace(/ /g, "-")}`}
                      className={`text-xs uppercase tracking-widest px-4 py-2.5 rounded-full border transition-colors ${size === s ? "bg-black text-white border-black" : "border-black/20 hover:border-black"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Qty */}
            <div className="flex items-center gap-4 mb-8">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Qty</p>
              <div className="flex items-center gap-1 border border-black/15 rounded-full">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-dec" className="w-9 h-9 flex items-center justify-center"><Minus size={13} /></button>
                <span className="w-6 text-center text-sm" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} data-testid="qty-inc" className="w-9 h-9 flex items-center justify-center"><Plus size={13} /></button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <button onClick={() => addItem(product, qty)} data-testid="add-to-cart-btn"
                className="flex-1 border border-black rounded-full py-4 uppercase text-xs tracking-[0.2em] hover:bg-black hover:text-white transition-colors">
                Add to Cart
              </button>
              <button onClick={buyNow} data-testid="buy-now-btn"
                className="flex-1 bg-black text-white rounded-full py-4 uppercase text-xs tracking-[0.2em] hover:bg-black/85 transition-colors">
                Buy Now
              </button>
              <button onClick={() => { toggle(product); toast.success(wished ? "Removed from wishlist" : "Added to wishlist"); }}
                data-testid="wishlist-toggle" aria-label="Wishlist"
                className="w-14 h-[54px] border border-black/15 rounded-full flex items-center justify-center hover:border-black transition-colors">
                <Heart size={18} className={wished ? "fill-black text-black" : ""} />
              </button>
            </div>

            {/* Features */}
            <div className="grid grid-cols-2 gap-3 my-8">
              {product.features?.map((f) => (
                <div key={f} className="flex items-center gap-2 text-sm bg-[#F5F5F5] rounded-2xl px-4 py-3">
                  <ShieldCheck size={15} className="text-black/70 shrink-0" /> {f}
                </div>
              ))}
            </div>

            {/* In the box */}
            {product.in_the_box?.length > 0 && (
              <div className="mb-6" data-testid="in-the-box">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Inside the Box</p>
                <ul className="space-y-2">
                  {product.in_the_box.map((b) => (
                    <li key={b} className="flex items-center gap-2 text-sm"><span className="w-1.5 h-1.5 rounded-full bg-gold" /> {b}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Trust badges */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-6" data-testid="trust-badges">
              {[
                [Truck, "Free Shipping"],
                [Lock, "Secure Payment"],
                [Gem, "Premium Quality"],
                [RefreshCw, "Easy Replacement"],
                [Sparkles, "Everyday Luxury"],
              ].map(([Icon, t]) => (
                <div key={t} className="flex flex-col items-center gap-1.5 text-center bg-[#F5F5F5] rounded-2xl py-3.5 px-2">
                  <Icon size={16} className="text-gold" strokeWidth={1.3} />
                  <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.08em] text-muted-foreground leading-tight">{t}</span>
                </div>
              ))}
            </div>

            {/* Accordion: specs + shipping */}
            <Accordion type="single" collapsible className="border-t border-black/10" data-testid="product-accordion">
              <AccordionItem value="specs" className="border-black/10">
                <AccordionTrigger className="text-sm uppercase tracking-[0.15em] py-5">Size Specifications</AccordionTrigger>
                <AccordionContent>
                  <dl className="grid grid-cols-2 gap-y-3 text-sm pb-4">
                    {Object.entries(product.specs || {}).map(([k, v]) => (
                      <React.Fragment key={k}>
                        <dt className="text-muted-foreground">{k}</dt>
                        <dd className="text-right">{v}</dd>
                      </React.Fragment>
                    ))}
                  </dl>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="ship" className="border-black/10">
                <AccordionTrigger className="text-sm uppercase tracking-[0.15em] py-5">Shipping & Returns</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-4 space-y-3">
                  <p className="flex items-center gap-2"><Truck size={15} /> Free insured shipping across India (2–4 days) & worldwide.</p>
                  <p className="flex items-center gap-2"><RefreshCw size={15} /> 30-day easy returns on unworn frames.</p>
                  <p className="flex items-center gap-2"><ShieldCheck size={15} /> 2-year warranty on every pair.</p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </div>

      {/* Editorial alternating sections */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32 space-y-20 md:space-y-32" data-testid="editorial">
        {EDITORIAL.map((e, i) => {
          const src = product.images[e.idx % product.images.length];
          return (
            <div key={e.t} className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
              <Reveal className={`md:col-span-6 ${i % 2 ? "md:order-2" : ""}`}>
                <div className="aspect-[5/4] rounded-3xl overflow-hidden zoom-frame bg-[#F5F5F5]">
                  <img src={src} alt={e.t} loading="lazy" className="w-full h-full object-cover" />
                </div>
              </Reveal>
              <Reveal delay={0.1} className={`md:col-span-5 ${i % 2 ? "md:order-1 md:col-start-1" : "md:col-start-8"}`}>
                <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">{String(i + 1).padStart(2, "0")}</p>
                <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-[1.05] mb-5">{e.t}</h2>
                <p className="text-muted-foreground leading-relaxed text-lg">{e.b}</p>
              </Reveal>
            </div>
          );
        })}
      </section>

      {/* Reviews */}
      <section className="bg-[#F5F5F5] py-24" data-testid="product-reviews">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <h2 className="font-display text-4xl tracking-tight mb-10">Reviews <span className="text-muted-foreground text-2xl">({reviews.length})</span></h2>
            <div className="space-y-6">
              {reviews.length === 0 && <p className="text-muted-foreground text-sm">Be the first to review this frame.</p>}
              {reviews.map((r) => (
                <div key={r.id} className="bg-white rounded-2xl p-6">
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: r.rating }).map((_, k) => <Star key={k} size={13} className="fill-black text-black" />)}
                  </div>
                  {r.title && <p className="font-display text-lg mb-2">{r.title}</p>}
                  <p className="text-muted-foreground leading-relaxed mb-3">{r.body}</p>
                  <p className="text-xs uppercase tracking-[0.2em]">{r.name} {r.location && <span className="text-muted-foreground">· {r.location}</span>}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <div className="bg-white rounded-3xl p-8 sticky top-24">
              <h3 className="font-display text-2xl mb-6">Write a review</h3>
              <form onSubmit={submitReview} className="space-y-3" data-testid="review-form">
                <input data-testid="review-name" placeholder="Your name" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-[#F5F5F5] rounded-full px-5 py-3 text-sm outline-none focus:ring-1 ring-black" />
                <div className="flex items-center gap-2 px-2">
                  <span className="text-xs uppercase tracking-widest text-muted-foreground mr-2">Rating</span>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button type="button" key={n} onClick={() => setForm({ ...form, rating: n })} data-testid={`review-star-${n}`}>
                      <Star size={18} className={n <= form.rating ? "fill-black text-black" : "text-black/25"} />
                    </button>
                  ))}
                </div>
                <input data-testid="review-title" placeholder="Headline" value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full bg-[#F5F5F5] rounded-full px-5 py-3 text-sm outline-none focus:ring-1 ring-black" />
                <textarea data-testid="review-body" placeholder="Tell us what you think…" rows={4} value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  className="w-full bg-[#F5F5F5] rounded-2xl px-5 py-3 text-sm outline-none focus:ring-1 ring-black resize-none" />
                <button type="submit" data-testid="review-submit"
                  className="w-full bg-black text-white rounded-full py-3.5 uppercase text-xs tracking-[0.2em] hover:bg-black/85 transition-colors">
                  Submit review
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-24" data-testid="related-products">
          <h2 className="font-display text-4xl md:text-5xl tracking-tight mb-10">You may also like</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {related.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
          </div>
        </section>
      )}
      {/* Recently viewed */}
      {recent.length > 0 && (
        <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-24" data-testid="pdp-recently-viewed">
          <h2 className="font-display text-3xl md:text-4xl tracking-tight mb-8">Recently viewed</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {recent.map((p, i) => <ProductCard key={p.slug} product={{ ...p, images: [p.image] }} index={i} />)}
          </div>
        </section>
      )}

      {/* Fullscreen image viewer */}
      {full && (
        <div className="fixed inset-0 z-[70] bg-white/98 flex flex-col" data-testid="fullscreen-viewer" onClick={() => setFull(false)}>
          <button className="absolute top-6 right-6 z-10 w-11 h-11 rounded-full border border-black/15 flex items-center justify-center" data-testid="fullscreen-close" aria-label="Close">
            <X size={20} />
          </button>
          <div className="flex-1 flex items-center justify-center p-8" onClick={(e) => e.stopPropagation()}>
            <img src={`${product.images[active]}?w=2000&q=95`} alt={product.name} className="max-w-full max-h-full object-contain" />
          </div>
          <div className="flex justify-center gap-3 pb-8" onClick={(e) => e.stopPropagation()}>
            {product.images.map((im, i) => (
              <button key={i} onClick={() => setActive(i)} className={`w-14 h-16 rounded-xl overflow-hidden bg-[#F5F5F5] border-2 ${active === i ? "border-gold" : "border-transparent"}`}>
                <img src={`${im}?w=160&q=70`} alt="" className="w-full h-full object-contain" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sticky mobile add-to-cart */}
      <div className="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-white/90 backdrop-blur-xl border-t border-black/10 px-3 py-3 flex items-center gap-2" data-testid="sticky-add-bar">
        <div className="flex-1 min-w-0">
          <p className="font-display text-sm truncate">{product.name}</p>
          <p className="text-xs text-muted-foreground">{formatINR(product.price)}</p>
        </div>
        <button onClick={() => addItem(product, qty)} data-testid="sticky-add-btn"
          className="shrink-0 bg-black text-white rounded-full px-5 py-3 uppercase text-[11px] tracking-[0.15em] whitespace-nowrap">Add to Cart</button>
      </div>
    </div>
  );
}
